/**
 * Generic resource API factory.
 *
 * Wraps apiClient with a CRUD shape so each domain module
 * (customers, orders, ...) only needs to declare its base path
 * plus any non-CRUD endpoints it adds on top.
 *
 * Backend response shapes vary slightly per module:
 *   - List: either `{ data: T[], pagination }` or a top-level array,
 *           or `{ <resource>: T[], pagination }` (e.g. `{ users, pagination }`).
 *   - Single: either `T` directly or `{ data: T }`.
 * The unwrap helpers below normalize all three so callers see a single shape.
 */

import { apiClient } from './api';

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  pages?: number;
  totalPages?: number;
}

export interface ListResponse<T> {
  data: T[];
  pagination?: PaginationMeta;
}

export interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  [key: string]: unknown;
}

function unwrapList<T>(raw: any, collectionKey?: string): ListResponse<T> {
  if (Array.isArray(raw)) {
    return { data: raw as T[] };
  }
  if (raw?.data && Array.isArray(raw.data)) {
    return { data: raw.data as T[], pagination: raw.pagination };
  }
  if (collectionKey && Array.isArray(raw?.[collectionKey])) {
    return { data: raw[collectionKey] as T[], pagination: raw.pagination };
  }
  // Sometimes the array is nested under a generic name like `items` / `results`.
  for (const key of ['items', 'results', 'records']) {
    if (Array.isArray(raw?.[key])) {
      return { data: raw[key] as T[], pagination: raw.pagination };
    }
  }
  return { data: [] };
}

function unwrapOne<T>(raw: any): T {
  if (raw?.data && typeof raw.data === 'object' && !Array.isArray(raw.data)) {
    return raw.data as T;
  }
  return raw as T;
}

export interface ResourceApi<T, CreateInput = Partial<T>, UpdateInput = Partial<T>> {
  list(params?: ListParams): Promise<ListResponse<T>>;
  get(id: number | string): Promise<T>;
  create(input: CreateInput): Promise<T>;
  update(id: number | string, input: UpdateInput): Promise<T>;
  remove(id: number | string): Promise<void>;
}

export interface CreateResourceApiOptions {
  /**
   * The plural key the backend uses to wrap collection responses,
   * e.g. `users` for the users endpoint that returns `{ users, pagination }`.
   * Optional — falls back to `data`, `items`, `results`, `records`.
   */
  collectionKey?: string;
  /**
   * If the backend uses a different update verb (PUT vs PATCH), override here.
   */
  updateMethod?: 'put' | 'patch';
}

export function createResourceApi<
  T,
  CreateInput = Partial<T>,
  UpdateInput = Partial<T>,
>(basePath: string, options: CreateResourceApiOptions = {}): ResourceApi<T, CreateInput, UpdateInput> {
  const { collectionKey, updateMethod = 'put' } = options;

  return {
    async list(params: ListParams = {}) {
      const raw = await apiClient.get<any>(basePath, params as Record<string, any>);
      return unwrapList<T>(raw, collectionKey);
    },

    async get(id) {
      const raw = await apiClient.get<any>(`${basePath}/${id}`);
      return unwrapOne<T>(raw);
    },

    async create(input) {
      const raw = await apiClient.post<any>(basePath, input);
      return unwrapOne<T>(raw);
    },

    async update(id, input) {
      const fn = updateMethod === 'patch' ? apiClient.patch.bind(apiClient) : apiClient.put.bind(apiClient);
      const raw = await fn<any>(`${basePath}/${id}`, input);
      return unwrapOne<T>(raw);
    },

    async remove(id) {
      await apiClient.delete<void>(`${basePath}/${id}`);
    },
  };
}
